import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import { toast } from "react-toastify";
// CreditNoteItems
// - Fetches items from: `${Base_URL}/CreditNote/invoiceItems/id?id=${creditNoteId}`
// - Shows items in a responsive table with:
//    1) a checkbox to select the item
//    2) an input for "CreditNote Price" per item (as string while editing to allow decimals)
//    3) a checkbox "Calculate GST" per item
// - When `calculateGst` is true, taxableValue, cgstAmount, sgstAmount, igstAmount and grossValue
//   are recalculated based on the creditNotePrice and quantity.

export default function CreditNoteItems() {
  const { creditNoteId } = useParams();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function fetchItems() {
      if (!creditNoteId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${Base_URL}/CreditNote/invoiceItems/id?id=${encodeURIComponent(creditNoteId)}`
        );
        const data = res.data || [];

        // normalize and extend each item with UI fields and numeric tax percents
        const normalized = data.map((it) => {
          const quantity = Number(it.quantity) || 0;
          const price = Number(it.price) || 0;
          const cgstPercent = Number(it.cgst) || 0; // assumed percent value like 2.5
          const sgstPercent = Number(it.sgst) || 0;
          const igstPercent = Number(it.igst) || 0;

          // store creditNotePrice as string for editing to allow decimal typing
          const creditNotePriceStr = price.toFixed(2);

          const taxableValue = Number(creditNotePriceStr) * quantity;
          const cgstAmount = taxableValue * (cgstPercent / 100);
          const sgstAmount = taxableValue * (sgstPercent / 100);
          const igstAmount = taxableValue * (igstPercent / 100);
          const grossValue = taxableValue + cgstAmount + sgstAmount + igstAmount;

          return {
            ...it,
            quantity,
            price,
            // store string for controlled input
            creditNotePrice: creditNotePriceStr,
            cgstPercent,
            sgstPercent,
            igstPercent,
            // derived numeric values
            taxableValue,
            cgstAmount,
            sgstAmount,
            igstAmount,
            grossValue,
            // UI fields
            selected: false,
            calculateGst: true,
          };
        });

        if (mounted) setItems(normalized);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load invoice items");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchItems();
    return () => (mounted = false);
  }, [creditNoteId]);

  // recalc expects creditNotePrice possibly as string; converts to Number
  function recalcItem(copy, idx) {
    const it = copy[idx];
    const q = Number(it.quantity) || 0;
    const price = Number(it.creditNotePrice) || 0; // parse string safely
    const cgstP = Number(it.cgstPercent) || 0;
    const sgstP = Number(it.sgstPercent) || 0;
    const igstP = Number(it.igstPercent) || 0;

    const taxable = price * q;
    let cgstAmt = 0,
      sgstAmt = 0,
      igstAmt = 0;
    if (it.calculateGst) {
      cgstAmt = taxable * (cgstP / 100);
      sgstAmt = taxable * (sgstP / 100);
      igstAmt = taxable * (igstP / 100);
    }
    const gross = taxable + cgstAmt + sgstAmt + igstAmt;

    copy[idx] = {
      ...it,
      taxableValue: taxable,
      cgstAmount: cgstAmt,
      sgstAmount: sgstAmt,
      igstAmount: igstAmt,
      grossValue: gross,
    };
  }

  function toggleSelect(index) {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], selected: !copy[index].selected };
      return copy;
    });
  }

  // keep value as string to allow typing decimals (".", "12.", etc.)
  function changeCreditNotePrice(index, value) {
    setItems((prev) => {
      const copy = [...prev];
      // normalize comma to dot for locales that might use comma
      const normalizedValue = typeof value === "string" ? value.replace(",", ".") : value;
      copy[index] = { ...copy[index], creditNotePrice: normalizedValue };
      // recalc derived values (recalcItem will parse string to Number)
      recalcItem(copy, index);
      return copy;
    });
  }
  function changeQuantity(index, value) {
  setItems((prev) => {
    const copy = [...prev];
    const qty = Number(value) || 0;

    copy[index] = {
      ...copy[index],
      quantity: qty,
    };

    recalcItem(copy, index);
    return copy;
  });
}


  function toggleCalculateGst(index) {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], calculateGst: !copy[index].calculateGst };
      // recalc derived values
      recalcItem(copy, index);
      return copy;
    });
  }

  function handleSelectAll(e) {
    const checked = e.target.checked;
    setItems((prev) => prev.map((it) => ({ ...it, selected: checked })));
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const selectedItems = items
        .filter((it) => it.selected)
        .map((it) => ({
          itemId: it.itemId,
          itemCode: it.item,
          quantity: it.quantity,
          originalPrice: it.price,
          // convert string to number for payload
          creditNotePrice: Number(it.creditNotePrice) || 0,
          calculateGst: !!it.calculateGst,
          batchId: it.batchId || null,
          hsn: it.hsn || null,
          taxableValue: Number(it.taxableValue) || 0,
          cgstAmount: Number(it.cgstAmount) || 0,
          sgstAmount: Number(it.sgstAmount) || 0,
          igstAmount: Number(it.igstAmount) || 0,
          grossValue: Number(it.grossValue) || 0,
        }));

      const payload = {
        creditNoteId: creditNoteId || null,
        organisationId: user?.organisationId ?? null,
        userId: user?.userId ?? null,
        items: selectedItems,
      };

      // POST to backend (endpoint provided in your code before)
      const res = await axios.post(`${Base_URL}/CreditNote/postCreditNoteItems`, payload);
      if(res.status==200)
      {
        toast.success("Credit Note Created");
      }
      console.log("server response", res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to prepare payload");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white shadow rounded-2xl">
      <h2 className="text-2xl font-semibold mb-4">Credit Note Items — {creditNoteId}</h2>

      {error && <div className="text-red-600 mb-3">{error}</div>}

      {loading ? (
        <div className="text-sm">Loading items...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="text-left bg-gray-50">
                  <th className="p-2 border">
                    <input type="checkbox" onChange={handleSelectAll} />
                  </th>
                  <th className="p-2 border">Item Code</th>
                  <th className="p-2 border">Batch</th>
                  <th className="p-2 border">HSN</th>
                  <th className="p-2 border">Quantity</th>
                  <th className="p-2 border">Price</th>
                  <th className="p-2 border">CreditNote Price</th>
                  <th className="p-2 border">Calculate GST</th>
                  <th className="p-2 border">Taxable Value</th>
                  <th className="p-2 border">CGST</th>
                  <th className="p-2 border">SGST</th>
                  <th className="p-2 border">IGST</th>
                  <th className="p-2 border">Gross Value</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.itemId} className="odd:bg-white even:bg-gray-50">
                    <td className="p-2 border align-top">
                      <input type="checkbox" checked={!!it.selected} onChange={() => toggleSelect(idx)} />
                    </td>
                    <td className="p-2 border align-top">{it.item}</td>
                    <td className="p-2 border align-top">{it.batchId || "-"}</td>
                    <td className="p-2 border align-top">{it.hsn || "-"}</td>
                    <td className="p-2 border align-top">
  <input
    type="number"
    min="0"
    step="1"
    className="w-20 border rounded p-1"
    value={it.quantity}
    onChange={(e) => changeQuantity(idx, e.target.value)}
  />
</td>

                    <td className="p-2 border align-top">{it.price}</td>
                    <td className="p-2 border align-top">
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        className="w-28 border rounded p-1"
                        value={it.creditNotePrice}
                        onChange={(e) => changeCreditNotePrice(idx, e.target.value)}
                      />
                    </td>
                    <td className="p-2 border align-top text-center">
                      <input type="checkbox" checked={!!it.calculateGst} onChange={() => toggleCalculateGst(idx)} />
                    </td>
                    <td className="p-2 border align-top">{Number(it.taxableValue).toFixed(2)}</td>
                    <td className="p-2 border align-top">{Number(it.cgstAmount).toFixed(2)}</td>
                    <td className="p-2 border align-top">{Number(it.sgstAmount).toFixed(2)}</td>
                    <td className="p-2 border align-top">{Number(it.igstAmount).toFixed(2)}</td>
                    <td className="p-2 border align-top">{Number(it.grossValue).toFixed(2)}</td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={13} className="p-4 text-center text-sm text-gray-500">
                      No items found for this credit note.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 rounded-2xl ${submitting ? "bg-gray-300" : "bg-blue-600 text-white"}`}
            >
              {submitting ? "Preparing..." : "Submit Selected Items"}
            </button>

            <button
              type="button"
              onClick={() => console.log("Current items state:", items)}
              className="px-3 py-2 rounded-2xl border"
            >
              Debug: Log Items
            </button>

            <div className="ml-auto text-sm text-gray-500">Total items: {items.length}</div>
          </div>
        </form>
      )}
    </div>
  );
}
